import React, { useContext } from 'react'
import styled, { css, ThemeContext } from 'styled-components'
import { useHistory } from 'react-router'
import { useTranslation } from 'react-i18next'

// Components
import StickyHeaderLayoverModal from '../feedback/StickyHeaderLayoverModal'
import Text from '../Text'
import Icon from '../icon/Icon'

// Styles
import { fastTransition } from '../../styles/effects'

// Utils
import { useAppState } from '../../context/AppContext'
import {
  getCompanySetupTasks,
  AI_SETUP_TASK_ORDER,
} from '../../utils/companySetupTasks'

// Types
import { CompanySetupTaskId } from '../../common/helpers/companySetup'

interface IProps {
  closeModal: () => void
}

// Support-only checklist that guides setting up a freshly-created company. Each row
// reflects live company state: a finished task shows a check and is inert, an open
// one routes to its settings screen (AI tasks auto-open their wizard). The AI steps
// are walked in order — a not-yet-done AI step is locked until every earlier AI step
// is done (out-of-order setup is still allowed directly in settings).
const CompanySetupChecklistModal: React.FC<IProps> = ({ closeModal }) => {
  const { t } = useTranslation()
  const history = useHistory()
  const theme = useContext(ThemeContext)
  const { company } = useAppState()

  if (!company) return null

  const tasks = getCompanySetupTasks(company)
  const doneById = Object.fromEntries(tasks.map((task) => [task.id, task.done]))

  const isLocked = (id: CompanySetupTaskId): boolean => {
    const index = AI_SETUP_TASK_ORDER.indexOf(id)
    if (index <= 0 || doneById[id]) return false
    return AI_SETUP_TASK_ORDER.slice(0, index).some((prior) => !doneById[prior])
  }

  return (
    <StickyHeaderLayoverModal
      closeModal={closeModal}
      headerConfig={{
        title: t('completeCompany.modalTitle'),
        subtitle: t('completeCompany.modalSubtitle'),
      }}
    >
      <List>
        {tasks.map((task) => {
          const locked = isLocked(task.id)
          return (
            <TaskRow
              key={task.id}
              locked={locked}
              onClick={
                locked
                  ? undefined
                  : () => {
                      // Done rows go to the settings page to review/edit; open
                      // rows keep the ?wizard= param so the generator auto-opens.
                      history.push(
                        task.done ? task.path.split('?')[0] : task.path
                      )
                      closeModal()
                    }
              }
            >
              <IconCircle done={task.done} locked={locked}>
                {task.done ? (
                  <Icon type="Check" size={18} color={theme.colors.lightest} />
                ) : (
                  <Icon
                    type={task.icon}
                    size={18}
                    color={
                      locked ? theme.colors.textLighter : theme.colors.primary
                    }
                  />
                )}
              </IconCircle>
              <Info>
                <Text
                  textStyle="labelHeavy"
                  color={locked ? 'textLight' : 'text'}
                >
                  {t(task.labelKey)}
                </Text>
                <Text textStyle="caption" color="textLighter">
                  {task.done
                    ? t('general.states.completed')
                    : locked
                    ? t('completeCompany.locked')
                    : t(task.descriptionKey)}
                </Text>
              </Info>
              <Trailing>
                {locked ? (
                  <Icon
                    type="Lock"
                    size={14}
                    color={theme.colors.textLighter}
                  />
                ) : (
                  <Icon
                    type="ChevronRight"
                    size={14}
                    color={theme.colors.textLighter}
                  />
                )}
              </Trailing>
            </TaskRow>
          )
        })}
      </List>
    </StickyHeaderLayoverModal>
  )
}

export default CompanySetupChecklistModal

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 24px;
`

// Three states: a locked row is muted with a lock and inert; a done or available
// row stays active (white) and clickable, lifting to primaryLight on hover. Done vs
// available is told apart by the icon circle (filled primary check vs tinted icon).
const TaskRow = styled.div<{ locked?: boolean; onClick?: () => void }>`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.textLightest};
  background-color: ${({ locked, theme }) =>
    locked ? theme.colors.textLightest : theme.colors.lightest};
  transition: background-color ${fastTransition}, border-color ${fastTransition};

  ${({ onClick }) =>
    onClick &&
    css`
      cursor: pointer;

      &:hover {
        background-color: ${({ theme }) => theme.colors.primaryLight};
        border-color: ${({ theme }) => theme.colors.primaryLight};
      }
    `}
`

// Done → filled primary (with a white check); locked → plain white (muted icon);
// available → primaryLight tint (primary icon).
const IconCircle = styled.div<{ done?: boolean; locked?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${({ done, locked, theme }) =>
    done
      ? theme.colors.primary
      : locked
      ? theme.colors.lightest
      : theme.colors.primaryLight};
`

const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const Trailing = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: auto;
`
